import { useCallback, useMemo } from 'react';

import { type CanvasQueryParams } from '~/constants/canvasQueryParams';
import { type QueryParamNames, QueryParamsWithArrayValue } from '~/constants/search';
import { useRouterSelector } from '~/hooks/useRouterSelector';
import { setQueryParams } from '~/utils/PathUtils';

export type AnyQueryParam = QueryParamNames | CanvasQueryParams;

/**
 * Helper type to get the correct value type based on whether it's an array param
 */
export type QueryParamValue<K extends AnyQueryParam> = K extends (typeof QueryParamsWithArrayValue)[number]
  ? string[]
  : string;

/**
 * Type guard to check if a param is an array param
 */
function isArrayParam(key: AnyQueryParam): key is (typeof QueryParamsWithArrayValue)[number] {
  return QueryParamsWithArrayValue.some((param) => param === key);
}

export type QueryParamValues<T extends readonly AnyQueryParam[]> = {
  [K in T[number]]: QueryParamValue<K>;
};

type ParamsValues = string | string[] | number | boolean | null;

/**
 * Hook to manage query parameters.
 * Returns values for all specified keys and a setter that updates them atomically.
 */
export const useRouterQueryState = <T extends readonly AnyQueryParam[]>(
  keys: T,
): {
  values: QueryParamValues<T>;
  setValues: (values: Partial<{ [K in T[number]]: ParamsValues }>) => void;
  getValues: () => QueryParamValues<T>;
} => {
  const getValuesFromLocation = useCallback(
    (location: Location) => {
      const params = new URLSearchParams(location.search);
      const result: Record<string, string | string[]> = {};

      for (const key of keys) {
        if (isArrayParam(key)) {
          const arrayValue = params.getAll(key);
          result[key] = arrayValue.length > 0 ? arrayValue : [];
        } else {
          result[key] = params.get(key) ?? '';
        }
      }

      return result as QueryParamValues<T>;
    },
    [keys],
  );

  const emptyQueryParamValues = useMemo<QueryParamValues<T>>(() => {
    const result: Record<string, string | string[]> = {};

    for (const key of keys) {
      if (isArrayParam(key)) {
        result[key] = [];
      } else {
        result[key] = '';
      }
    }

    return result as QueryParamValues<T>;
  }, [keys]);

  const values = useRouterSelector((location) => getValuesFromLocation(location)) ?? emptyQueryParamValues;

  const setValues = useCallback((newValues: Partial<{ [K in T[number]]: ParamsValues }>) => {
    setQueryParams(newValues, 'replace', false);
  }, []);

  const getValues = useCallback(() => getValuesFromLocation(window.location), [getValuesFromLocation]);

  return { values, setValues, getValues };
};
